import type { ComponentType, SVGProps } from 'react';
import type { NotificationTypeValue } from '@echo-portal/shared';
import {
  CheckIcon,
  ChatBubbleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@/components/icons';

// The icon shim re-exports Boxicons under SVG element types so they
// can take any SVG attribute. Width/height accept string OR number.
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * The four visual categories the 15 notification types collapse into
 * for the avatar-corner status badge in the notification tray. The
 * mapping is purely cosmetic — it doesn't affect routing or backend
 * semantics, only the icon + colour shown on each row's avatar.
 */
type BadgeCategory = 'success' | 'added' | 'alert' | 'info';

interface BadgeStyle {
  icon: IconComponent;
  /** Solid fill of the circular badge background. */
  background: string;
  /** Foreground used for the icon. White against the saturated solid bg. */
  foreground: string;
}

// Each glyph is chosen so it doesn't carry its own circle outline —
// the coloured disc beneath already provides the circular framing,
// and a glyph-internal circle (e.g. CheckCircled, PlusCircled) would
// double up and look busy. Bare `Check`, `Plus`, triangle, bubble.
const CATEGORY_STYLE: Record<BadgeCategory, BadgeStyle> = {
  success: { icon: CheckIcon, background: 'var(--green-9)', foreground: 'white' },
  added: { icon: PlusIcon, background: 'var(--blue-9)', foreground: 'white' },
  alert: { icon: ExclamationTriangleIcon, background: 'var(--red-9)', foreground: 'white' },
  info: { icon: ChatBubbleIcon, background: 'var(--gray-9)', foreground: 'white' },
};

// 15 notification types → 4 badge categories. Anything missing falls
// through to `info` (a neutral chat-bubble glyph) so a freshly added
// type never crashes the tray.
const TYPE_TO_CATEGORY: Partial<Record<NotificationTypeValue, BadgeCategory>> = {
  review_approved: 'success',
  review_comment_resolved: 'success',
  content_published: 'success',
  branch_ready_to_publish: 'success',

  reviewer_added: 'added',
  collaborator_added: 'added',

  review_changes_requested: 'alert',
  ai_compliance_error: 'alert',
};

interface NotificationBadgeProps {
  type: NotificationTypeValue;
  /** Outer diameter in px. Defaults to 16 — matches the avatar overlay
   *  in the popover row. */
  size?: number;
}

/**
 * Small circular status badge that overlays the bottom-right of a
 * notification row's actor avatar. Maps the notification type to one
 * of four colour/icon pairs and renders a coloured disc with a 2-px
 * white ring (so the badge separates cleanly from any avatar tone).
 */
export function NotificationBadge({ type, size = 16 }: NotificationBadgeProps) {
  const category = TYPE_TO_CATEGORY[type] ?? 'info';
  const { icon: Icon, background, foreground } = CATEGORY_STYLE[category];
  // Inner icon sits ~62% of the disc — large enough to read at 16px,
  // small enough to keep breathing room inside the white ring.
  const iconSize = Math.round(size * 0.62);

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        color: foreground,
        // 2-px ring matches the design — separates the badge from the
        // avatar without competing with the badge colour itself.
        boxShadow: '0 0 0 2px var(--color-background)',
        flexShrink: 0,
      }}
    >
      <Icon width={iconSize} height={iconSize} />
    </span>
  );
}

export default NotificationBadge;
